// ─────────────────────────────────────────────────────────────────────────────
// runSurface.ts — ONE RUN CONTROL, TWO AGENTS. Pure: no React, no fetch.
//
// Owner ruling 2026-09-14: "IT IS THE SAME CONTROL FOR BUCK AND DELTA. One
// component, two agents. THE STATES ARE SHARED AND THE MEANING OF EACH MUST HOLD
// FOR BOTH." Buck walks fifty a lane, ten a round, and never ends; Delta takes one
// company through two tabs and terminates at the script.
//
// ── THE SHARED WORDS ARE READ OFF EACH PRODUCER BY A COMPLETE TABLE ─────────
// `BUCK_STATE` and `DELTA_STATE` are `Record`s over each producer's OWN union, so
// a producer word added to either client without a row here is a COMPILE error,
// not a silent nearest neighbour. Nothing is inferred from `armed`/`running`/
// counts: each client has already refused a word it does not know (it reads
// `unknown`/`unread`), and this table only renames what was published.
//
//   shared      Buck (growth_runner)        Delta (delta_run_state)
//   running     walking                     running
//   stopping    stopping                    — not published
//   halted      halted  (an outage)         — NOT PUBLISHED: Delta has no outage word
//   idle        armed, not walking          idle (queue depth beside it)
//   off         off                         — Delta has no consent toggle
//   complete    — NOT A STATE FOR BUCK      complete (A COMPANY finished)
//   stopped     —                           stopped (ended short of the script)
//   failed      —                           failed
//   paused      — NOT PUBLISHED             — NOT PUBLISHED
//
// "COMPLETE" MEANS A UNIT FINISHED, AND THE UNIT IS THE AGENT'S. For Delta the
// unit is a company and the producer says `complete`. For Buck the unit is a
// ROUND, and his producer publishes it as a fact INSIDE a running walk
// (`live.round` of `live.roundsTarget`), never as a state word — his walk does not
// stop when a round banks. So Buck never enters `complete`; his round completion
// is a menu row, in his producer's own numbers.
//
// ── PAUSE IS NOT STOP, AND NEITHER PRODUCER CAN PAUSE ──────────────────────
// Stop ENDS the walk/run. Pause would HOLD it where it is, queue and position
// intact, and resume where it was. Neither route publishes `paused` and neither
// accepts a pause action, so PAUSE and RESUME render, disabled, with the gap
// named on the control. `paused` is in the shared vocabulary so the surface has
// a place for it, and it is UNREACHABLE from both tables today: a producer that
// starts emitting the word reads UNREAD until its client is taught it 1:1.
// ─────────────────────────────────────────────────────────────────────────────

import { RunnerStatus } from '../api/growthRunner';
import { DeltaRunnerState, canRun, canStop, runnerCaption } from '../api/deltaRunner';
import { DeltaToday } from '../api/deltaNeedsRun';
import { RunControlView } from './runControl';
import { buckStrip } from './buckLane';

export type RunAgentKind = 'buck' | 'delta';

export type SharedRunState =
  | 'unread' | 'absent' | 'off' | 'idle' | 'running' | 'stopping'
  | 'paused' | 'complete' | 'stopped' | 'halted' | 'failed';

/** Buck's words → shared. Complete over `RunnerStatus['state']`. */
export const BUCK_STATE: Record<RunnerStatus['state'], SharedRunState> = {
  walking: 'running',
  stopping: 'stopping',
  halted: 'halted',
  armed_not_walking: 'idle',
  off: 'off',
  unknown: 'unread',
};

/** Delta's words → shared. Complete over `DeltaRunnerState['state']`. */
export const DELTA_STATE: Record<DeltaRunnerState['state'], SharedRunState> = {
  unread: 'unread',
  absent: 'absent',
  idle: 'idle',
  running: 'running',
  complete: 'complete',
  stopped: 'stopped',
  failed: 'failed',
};

/**
 * THE MARK BESIDE THE CAPTION. Shape carries the states that must read apart at a
 * glance without colour or a tooltip.
 *
 * HALTED KEEPS ITS DIAMOND (owner ruling 2026-09-14): adding buttons must not
 * soften the one state that means something is broken. OFF is a disc.
 */
export type RunMark = 'diamond' | 'disc' | 'ring';

export type RunTone = 'danger' | 'go' | 'wait' | 'done' | 'muted' | 'unread';

export function markFor(state: SharedRunState): RunMark {
  if (state === 'halted') return 'diamond';
  if (state === 'running' || state === 'stopping' || state === 'off'
      || state === 'failed' || state === 'complete') return 'disc';
  return 'ring';
}

export function toneFor(state: SharedRunState): RunTone {
  switch (state) {
    case 'halted': case 'failed': case 'off': return 'danger';
    case 'running': return 'go';
    case 'stopping': case 'stopped': case 'paused': return 'wait';
    case 'complete': return 'done';
    case 'unread': case 'absent': return 'unread';
    default: return 'muted';
  }
}

/** One button. `why` is a few words, shown as the title and — for a gap — on the bar. */
export interface RunButton {
  enabled: boolean;
  label: string;
  why: string;
}

/** A row in the dropdown. EXACTLY ONE of `value` / `gap`: a fact, or the name of
 *  what the producer does not publish. A null fact is not rendered as a dash. */
export interface RunMenuRow {
  key: string;
  label: string;
  value?: string;
  gap?: string;
}

export interface RunSurface {
  agent: RunAgentKind;
  state: SharedRunState;
  caption: string;
  mark: RunMark;
  tone: RunTone;
  /** The producer's own sentence, when it has one. */
  detail: string;
  run: RunButton;
  stop: RunButton;
  /** ABSENT → THE HOLD GROUP IS NOT RENDERED. Delta's runner publishes no pause,
   *  and the owner ruled (2026-09-28) "Resume not published either works or goes":
   *  for Delta it goes. Buck's bar still names his gap. */
  pause?: RunButton;
  resume?: RunButton;
  /** The gap on the bar itself, so "not published" is not hidden in a menu. */
  pauseGap?: string;
  menu: RunMenuRow[];
  /** A LINE ABOVE THE BUTTONS for what the buttons alone cannot say — Delta's
   *  overnight loop. Absent for Buck: his bar renders exactly as before. */
  banner?: { text: string; tone: RunTone } | null;
}

export const NOT_PUBLISHED = 'NOT PUBLISHED';
export const PAUSE_GAP = 'PAUSE · RESUME NOT PUBLISHED';

const PAUSE: RunButton = { enabled: false, label: 'PAUSE',
  why: 'HOLDS IT WHERE IT IS — NOT PUBLISHED BY THIS RUNNER' };
const RESUME: RunButton = { enabled: false, label: 'RESUME',
  why: 'CONTINUES FROM WHERE IT WAS — NOT PUBLISHED BY THIS RUNNER' };

function fact(key: string, label: string, value: string | null | undefined): RunMenuRow[] {
  const v = value === null || value === undefined ? '' : String(value).trim();
  return v ? [{ key, label, value: v }] : [];
}

function gap(key: string, label: string, why: string = NOT_PUBLISHED): RunMenuRow {
  return { key, label, gap: why };
}

// ── BUCK ─────────────────────────────────────────────────────────────────────

export interface BuckSurfaceInput {
  runner: RunnerStatus | null;
  /** `resolveRunControl`'s view — the ONE authority on which press is offered. */
  view: RunControlView;
  now?: number;
}

export function buckSurface({ runner, view, now = Date.now() }: BuckSurfaceInput): RunSurface {
  const raw: RunnerStatus['state'] = runner && runner.reachable ? runner.state : 'unknown';
  const state = BUCK_STATE[raw];
  const offers = (a: 'arm' | 'stop') => view.actionable && (view.action === a || view.alsoOffers === a);

  const caption =
    state === 'running' ? 'RUNNING' :
    state === 'stopping' ? 'STOPPING' :
    state === 'halted' ? 'HALTED' :
    state === 'idle' ? (runner?.coolingUntil ? 'IDLE · COOLING' : 'IDLE · ARMED') :
    state === 'off' ? 'OFF' : 'UNREAD';

  const menu: RunMenuRow[] = [];
  const L = runner?.live || null;
  // ── THE CYCLE (owner ruling 2026-09-28) — shown walking or not, because
  // his place survives Off and a restart. The live company's cycle wins while
  // he walks; the stored position otherwise. Every figure is the producer's.
  const C = (L && L.cycle) || (runner && runner.reachable ? runner.cycle : null);
  if (C) {
    menu.push(...fact('cycle', 'CYCLE',
      [C.state, C.trade].filter(Boolean).join(' · ').toUpperCase() || null));
    if (C.tradeNumber !== null) {
      menu.push({ key: 'trade', label: 'TRADE', value: C.trades !== null
        ? `${C.tradeNumber} OF ${C.trades}` : `${C.tradeNumber}` });
    }
    if (C.newThisTrade !== null) {
      menu.push({ key: 'new', label: 'NEW THIS TRADE', value: C.perTrade !== null
        ? `${C.newThisTrade} OF ${C.perTrade}` : `${C.newThisTrade}` });
    }
    if (C.newThisState !== null) {
      menu.push({ key: 'state', label: 'NEW THIS STATE', value: C.stateQuota !== null
        ? `${C.newThisState} OF ${C.stateQuota}` : `${C.newThisState}` });
    }
  } else if (runner && runner.reachable && !runner.cycleKnown) {
    menu.push(gap('cycle', 'CYCLE', 'NOT PUBLISHED BY THIS BUILD'));
  }
  // WHAT BUCK PUBLISHES, AND ONLY WHILE HE WALKS — `live` is present only then.
  if (runner && runner.reachable && runner.liveKnown && L) {
    menu.push(...fact('lane', C ? 'SEARCHING' : 'LANE', L.laneLabel
      || [L.lane.state, L.lane.metro_region, L.lane.suburb_cluster, L.lane.sector].filter(Boolean).join(' · ')));
    // THE LANE MODEL'S "ROUND N OF 5" — only from a build that still sends it.
    if (!C && L.round !== null) {
      menu.push({ key: 'unit', label: 'ROUND', value: L.roundsTarget !== null
        ? `${L.round} OF ${L.roundsTarget} COMPLETE` : `${L.round} COMPLETE` });
    }
    if (!C && L.companiesComplete !== null) {
      menu.push({ key: 'progress', label: 'THIS ROUND', value: L.companiesInRound !== null
        ? `${L.companiesComplete} OF ${L.companiesInRound} DONE` : `${L.companiesComplete} DONE` });
    }
    menu.push(...fact('why', 'WHY THIS WORK', L.lastReason));
  } else if (runner && runner.reachable && !runner.liveKnown) {
    menu.push(gap('lane', 'LANE', 'NOT PUBLISHED BY THIS BUILD'));
  }
  if (runner?.halt?.alert) menu.push({ key: 'outage', label: 'OUTAGE', value: runner.halt.alert });
  if (runner?.coolingUntil) menu.push({ key: 'cooling', label: 'COOLING UNTIL', value: runner.coolingUntil });
  if (runner?.last) {
    menu.push(...fact('last', 'LAST WALK', runner.last.stoppedByOwner ? 'YOU STOPPED HIM' : runner.last.stopped));
  }
  // ── WHERE HE IS INSIDE THE ROUND (owner ruling 2026-09-15) ───────────────
  // Both rows read NOT PUBLISHED through a whole live walk, so the owner could
  // not tell a working run from a hung one. The producer now publishes the pair;
  // a build that does not is still named as a gap, never guessed at.
  if (runner && runner.reachable && runner.positionKnown) {
    if (state === 'running' || state === 'stopping') {
      const called = (c: RunnerStatus['queue'][number]) => c.name || c.companyId;
      // NULL WHILE WALKING IS THE PRODUCER'S "BETWEEN ROUNDS": the round banked
      // and the harness is choosing the next. It is a fact, not a gap.
      menu.push({ key: 'company', label: 'CURRENT COMPANY',
                  value: runner.currentCompany ? called(runner.currentCompany) : 'BETWEEN ROUNDS' });
      const q = runner.queue;
      menu.push({ key: 'queue', label: 'QUEUE',
                  value: `${q.length} QUEUED${q.length ? ' · ' + q.map(called).join(' · ') : ''}` });
    }
  } else {
    // WHAT THIS BUILD DOES NOT PUBLISH — named, not designed around.
    menu.push(gap('queue', 'QUEUE'));
    menu.push(gap('company', 'CURRENT COMPANY'));
  }

  return {
    agent: 'buck', state, caption, mark: markFor(state), tone: toneFor(state),
    detail: buckStrip(runner, now).line,
    run: { enabled: offers('arm'), label: state === 'halted' ? 'RETRY' : 'RUN',
           why: state === 'halted' ? 'RE-ARMS HIM — CLEARS THE HALT' : 'ARMS HIM — A CHECK EVERY 60s STARTS HIS LANE' },
    stop: { enabled: offers('stop'), label: 'STOP', why: 'ENDS THE WALK AT THE ROUND BOUNDARY' },
    pause: PAUSE, resume: RESUME, pauseGap: PAUSE_GAP,
    menu,
  };
}

// ── DELTA ────────────────────────────────────────────────────────────────────

/** THE LOOP'S WORDS ON THE RUN CONTROL (owner ruling 2026-09-17). The owner
 *  pressed STOP on what looked like a manual run and ended the night — "A
 *  DESTRUCTIVE BUTTON THAT LOOKS ROUTINE IS THE FAULT, NOT THE CLICK." */
export const LOOP_STOP_LABEL = 'STOP THE LOOP';
export const LOOP_CONFIRM_LABEL = 'CONFIRM — END THE LOOP';
export const LOOP_RUN_REFUSED = 'THE LOOP IS RUNNING — STOP IT FIRST';

export interface DeltaSurfaceInput {
  runner: DeltaRunnerState;
  hasCompany: boolean;
  sending: boolean;
  /** The first STOP press while the loop is armed has been made; the next ends it. */
  confirmingStop?: boolean;
  /** TODAY'S TRUTH from the store (`/api/delta/needs-run`) — null until read. */
  today?: DeltaToday | null;
}

/** THE STRIP'S TODAY LINE (owner ruling 2026-09-28): "v2 scripts, thin, on the call
 *  list — not the old run's figures". */
export function todayLine(t: DeltaToday): string {
  return `TODAY · ${t.v2Scripts} V2 SCRIPTS · ${t.thin} THIN · ${t.onCallList} ON THE CALL LIST`
    + ` · ${t.needARun} NEED A RUN` + (t.v1ToReplace ? ` · ${t.v1ToReplace} ON v1` : '');
}

export function deltaSurface({ runner, hasCompany, sending, confirmingStop = false, today = null }: DeltaSurfaceInput): RunSurface {
  const state = DELTA_STATE[runner.state];
  const runOk = canRun(runner.state, hasCompany) && !sending;
  const menu: RunMenuRow[] = [];
  if (runner.state === 'complete') {
    menu.push(...fact('unit', 'COMPANY COMPLETE', runner.companyId));
  } else {
    menu.push(...fact('company', 'COMPANY', runner.companyId));
  }
  if (runner.depthKnown) {
    const q = runner.queue && runner.queue.length ? ` · ${runner.queue.join(' · ')}` : '';
    menu.push({ key: 'queue', label: 'QUEUE', value: `${runner.queueDepth ?? 0} QUEUED${q}` });
  } else if (runner.state !== 'unread' && runner.state !== 'absent') {
    menu.push(gap('queue', 'QUEUE', 'NOT PUBLISHED BY THIS BUILD'));
  }
  menu.push(...fact('lane', 'ASKING', runner.lane));
  menu.push(...fact('started', 'STARTED', runner.startedAt));
  menu.push(...fact('last', 'LAST RUN', runner.lastStopped));
  // HALTED IS AN OUTAGE FOR EITHER AGENT — and Delta's runner has no word for it.
  menu.push(gap('outage', 'OUTAGE STATE', 'NOT PUBLISHED FOR DELTA'));

  const loop = runner.loop || null;
  const holding = !!loop && (loop.armed || loop.finishing);
  if (loop && loop.lastStop && !holding) {
    menu.push(...fact('loop-stopped', 'LOOP STOPPED', loop.lastStop.at));
    // THE REASON MOVES HERE WHEN TODAY'S LINE TAKES THE BANNER — still one press away.
    if (today) menu.push(...fact('loop-stopped-why', 'LOOP STOPPED BECAUSE', loop.lastStop.reason));
  }
  const base: RunSurface = {
    agent: 'delta', state, caption: runnerCaption(runner), mark: markFor(state), tone: toneFor(state),
    // A REFUSED COMMAND SAYS WHY, IN THE PRODUCER'S SENTENCE, where the run line is.
    detail: runner.refusal || runner.detail,
    run: { enabled: runOk, label: 'RUN',
           why: hasCompany ? 'RESEARCHES THE COMPANY AND WRITES THE SCRIPT' : 'SELECT A COMPANY' },
    stop: { enabled: canStop(runner.state) && !sending, label: 'STOP', why: 'ENDS THE RUN' },
    // NO PAUSE GROUP FOR DELTA — it is not published, so it goes (ruling 2026-09-28).
    menu,
    banner: today ? { text: todayLine(today), tone: 'muted' } : null,
  };
  if (!loop) return base;
  if (loop.armed) {
    // ⭐⭐⭐ WHILE THE LOOP HOLDS THE BOX THE CONTROL SAYS SO, AND ITS STOP ENDS THE
    // LOOP — in words, and behind a second press. RUN is refused rather than
    // stacked: a run queued silently behind the loop is how Glenmount was walked.
    const who = loop.business || loop.companyId || 'between companies';
    return {
      ...base,
      banner: { text: 'OVERNIGHT LOOP · ' + who + (loop.summary ? ' · ' + loop.summary : '')
                      + (loop.thisRun ? ' — ' + loop.thisRun : ''), tone: 'wait' },
      run: { enabled: false, label: 'RUN', why: LOOP_RUN_REFUSED },
      stop: confirmingStop
        ? { enabled: !sending, label: LOOP_CONFIRM_LABEL, why: 'PRESS AGAIN TO END THE WHOLE LOOP' }
        : { enabled: !sending, label: LOOP_STOP_LABEL, why: 'ENDS THE WHOLE LOOP, NOT JUST THIS COMPANY' },
    };
  }
  if (loop.finishing) {
    return {
      ...base,
      banner: { text: 'LOOP STOPPING · FINISHING ' + (loop.business || loop.companyId || 'ITS COMPANY'), tone: 'wait' },
      run: { enabled: false, label: 'RUN', why: 'THE LOOP IS FINISHING ITS COMPANY' },
    };
  }
  if (loop.lastStop) {
    const bad = loop.lastStop.kind === 'halted' || loop.lastStop.kind === 'failed';
    // A HALT OR A FAILURE KEEPS THE BANNER — an outage is never hidden behind a
    // count. An ordinary stop gives way to today's line once today is read.
    if (today && !bad) return base;
    return {
      ...base,
      banner: { text: 'LOOP STOPPED · ' + loop.lastStop.reason
                      + (loop.lastStop.summary ? ' · ' + loop.lastStop.summary : '')
                      + (loop.lastStop.thisRun ? ' — ' + loop.lastStop.thisRun : ''),
                tone: bad ? 'danger' : 'muted' },
    };
  }
  return base;
}
